import React, {FC, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import { useDebouncedCallback } from "@mantine/hooks";
import {useMutation, useQuery} from "@tanstack/react-query";
import {FieldSetting, getFieldSettings} from "@/PageComponents/Settings/Field Settings/FieldSettings";
import * as Enums from "@/utils/enums";
import {
    getFormNameForSystemName,
    getSystemNameForFormName
} from "@/PageComponents/Settings/Field Settings/fieldSettingHelper";
import Time from "@/utils/time";
import {Anchor, Box, Button, Flex, Group, SimpleGrid, Space, Title} from "@mantine/core";
import CustomerContactLocationSelector from "@/components/selectors/customer/customer-contact-location-selector";
import InventorySelector from "@/components/selectors/inventory/inventory-selector";
import ScTextControl from "@/components/sc-controls/form-controls/v2/sc-text-control";
import SCDatePicker from "@/components/sc-controls/form-controls/sc-datepicker";
import Link from "next/link";
import {IconQuestionCircle} from "@tabler/icons";
import {useForm} from "@mantine/form";
import Helper from "@/utils/helper";
import StoreSelector from "@/components/selectors/store/store-selector";
import Fetch from "@/utils/Fetch";
import Storage from "@/utils/storage";
import {showNotification} from "@mantine/notifications";
import SubscriptionContext from "@/utils/subscription-context";
import BillingService from "@/services/billing-service";
import PS from "@/services/permission/permission-service";
import QueryTypeSelector from "@/components/selectors/query/query-type-selector";
import QueryStatusSelector from "@/components/selectors/query/query-status-selector";
import EmployeeSelector from "@/components/selectors/employee/employee-selector";
import SCDropdownList from "@/components/sc-controls/form-controls/sc-dropdownlist";
import SCTextArea from "@/components/sc-controls/form-controls/sc-textarea";
import SCSwitch from "@/components/sc-controls/form-controls/sc-switch";
import ScNumberControl from "@/components/sc-controls/form-controls/v2/sc-number-control";
import ConfirmAction from "@/components/modals/confirm-action";
import Router, {useRouter} from "next/router";
import InventoryItemModal from "@/PageComponents/Inventory/InventoryItemModal";

const _userAdmin = PS.hasPermission(Enums.PermissionName.MasterOfficeAdmin);

const queryPriorities = Enums.getEnumItems(Enums.QueryPriority, false);

const queryPost = async (params, put: boolean) => {
    const req = put ? Fetch.put : Fetch.post
    const res =  await req({
        url: `/Query`,
        params,
    } as any)

    if(res?.Query?.ID || res?.ID) {
        return res
    } else {
        throw new Error(res.serverMessage || res.message || 'something went wrong')
    }
}

const QueryForm: FC<{
    query?: any
    job?: any
    module?: number
    moduleID?: string
    customerID?: number
    onCustomerChange?: (newCustomer: any) => void
    hideSaveAndCancel?: boolean
    // triggerSave?: number
    onSubmitState?: (state: 'success' | 'error') => void
    setSubmitState?: 'onSubmit' | 'none'
    onSaved?: (query: any) => void
    onClose?: () => void
}> = (props) => {

    const subscriptionContext = useContext(SubscriptionContext);
    const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
    const [hasCreditsAvailable, setHasCreditsAvailable] = useState<any>();

    const [showCreateNewInventory, setShowCreateNewInventory] = useState(false);

    const getAccessStatus = async () => {
        const [subscriptionInfo, message] = await BillingService.getSubcriptionInfo(subscriptionContext);
        if (subscriptionInfo) {
            setHasCreditsAvailable(subscriptionInfo.SMSCreditsPurchased - (subscriptionInfo.SMSCreditsUsed ? subscriptionInfo.SMSCreditsUsed : 0) > 0);
            setAccessStatus(subscriptionInfo.AccessStatus);
        }
    };

    const querySettings = useQuery(['assetFieldSettings'], () => getFieldSettings(Enums.Module.Query))
    const settingsBySystemName: { [fieldSystemName: string]: FieldSetting } = useMemo(() => {
        if (querySettings.data) {
            return querySettings.data.reduce((previousValue, currentValue) => ({
                ...previousValue,
                [currentValue.FieldSystemName]: {...currentValue}
            }), {})
        } else {
            return {}
        }
    }, [querySettings.data])
    const isRequired = useCallback((name: string) => {
        const systemName = getSystemNameForFormName(name)
        return settingsBySystemName.hasOwnProperty(systemName) ? isShown(name) && settingsBySystemName[systemName].IsRequired : false
    }, [settingsBySystemName])

    const isShown = useCallback((name: string) => {
        const systemName = getSystemNameForFormName(name)
        return settingsBySystemName.hasOwnProperty(systemName) ? settingsBySystemName[systemName].IsActive && (!!props.query || !settingsBySystemName[systemName].HideOnCreate) : false
    }, [settingsBySystemName, props.query])

    const formInitialValues = useMemo(() => {
        return {
            CustomerID: props.query ? props.query?.CustomerID : props.job?.CustomerID || null,
            CustomerContactID: props.query ? props.query?.CustomerContactID : props.job?.CustomerContactID || null,
            LocationID: props.query ? props.query?.LocationID : props.job?.LocationID || null,
            InventoryID: props.query?.Inventory?.ID || null,
            QueryTypeID: props.query ? props.query.QueryTypeID : null,
            QueryStatusID: props.query ? props.query.QueryStatusID : null,
            Priority: props.query ? props.query?.Priority : null, // Enums.QueryPriority[selectedQueryPriority],
            Reference: props.query ? props.query.Reference : '',
            Description: props.query ? props.query.Description : '',
            EmployeeID: props.query ? props.query.EmployeeID : null,
            // Date: Time.today(),
            FollowupDate: props.query ? props.query.FollowupDate : null,
            // ClientDate: props.query ? props.query.ClientDate : Time.today(),
            SendEmail: props.query ? props.query.SendEmail : true,
            SendSMS: props.query ? props.query.SendSMS : true,
            StoreID: props.query ? props.query.StoreID : null,
            IsDraft: props.query ? props.query.IsDraft : true,
            CustomField1: props.query?.CustomField1 || '',
            CustomField2: props.query?.CustomField2 || '',
            CustomField3: props.query?.CustomField3 || '',
            CustomField4: props.query?.CustomField4 || '',
            CustomFilter1: props.query?.CustomFilter1 || false,
            CustomFilter2: props.query?.CustomFilter2 || false,
            CustomDate1: props.query?.CustomDate1 /*&& dayjs(props.query?.CustomDate1).utc(true).toDate()*/ || null,
            CustomDate2: props.query?.CustomDate2 /*&& dayjs(props.query?.CustomDate2).utc(true).toDate()*/ || null,
            CustomNumber1: props.query?.CustomNumber1 || null,
            CustomNumber2: props.query?.CustomNumber2 || null,
        }
    }, [props.query, props.job])

    const form = useForm({
        initialValues: formInitialValues,
        validate: {
            StoreID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: isMultiStore
            } as any),
            FollowupDate: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Date,
                required: true
            } as any),
            InventoryID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: isRequired('InventoryID')
            } as any),
            CustomerID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: true
            } as any),
            CustomerContactID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: true
            } as any),
            LocationID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: isRequired('LocationID')
            } as any),
            QueryTypeID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: true
            } as any),
            QueryStatusID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: true
            } as any),
            Priority: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Number,
                required: true
            } as any),
            Description: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: true
            } as any),
            EmployeeID: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: true
            } as any),
            CustomField1: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: isRequired('CustomField1'),
            } as any),
            CustomField2: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: isRequired('CustomField2'),
            } as any),
            CustomField3: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: isRequired('CustomField3'),
            } as any),
            CustomField4: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Text,
                required: isRequired('CustomField4'),
            } as any),
            CustomNumber1: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Number,
                required: isRequired('CustomNumber1'),
            } as any),
            CustomNumber2: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Number,
                required: isRequired('CustomNumber2'),
            } as any),
            CustomDate1: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Date,
                required: isRequired('CustomDate1')
            } as any),
            CustomDate2: (x) => Helper.validateInputStringOut({
                value: x,
                controlType: Enums.ControlType.Date,
                required: isRequired('CustomDate2')
            } as any)
        }
    })

    useEffect(() => {
        // in order to update form state when item is updated from outside with uncontrolled values like isDraft so that isDirty calculation remains relevant
        if(formInitialValues) {
            form.setValues(formInitialValues)
        }
    }, [formInitialValues]);

    const isDirty = useRef(false)

    const debouncedMutate = useDebouncedCallback(
        (payload: { Query: any; ItemID: any; Module: any }) => queryMutation.mutate(payload as any),
        500
    )

    // const [isDirty, setIsDirty] = useState(false)
    useEffect(() => {
        const ignore = ['StoreID']

        const dirtyItems = Object.entries(form.values).filter(([key, value]) =>
            !ignore.includes(key) && (typeof value === 'string' ? value.trim() !== formInitialValues[key] : value !== formInitialValues[key])
        )
        if(dirtyItems.length !== 0) {
            if(form.isDirty()) {
                isDirty.current = true
            }
        } else {
            isDirty.current = false
        }
    }, [formInitialValues, form.values]);

    const scrollToErrors = () => {
        setTimeout(() => {
            const errors = Object.entries(form.validate().errors)

            if(errors.length !== 0) {
                props.onSubmitState && props.onSubmitState('error')

                const [, error] = errors[0]

                showNotification({
                    id: 'fieldErrors',
                    title: 'There are errors on the page',
                    message: error,
                    color: 'yellow',
                    autoClose: 2000
                })

                const el = document.querySelectorAll('[data-error]')
                if (el.length !== 0) {
                    el[0]?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    })
                }
            }
        }, 100)
    }

    /** Confirm Options*/
    const router = useRouter()
    const navUrl = useRef('')
    const saveAndNavigate = (url: string) => {
        navUrl.current = url
        handleSubmit(form.values)
    }
    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
    Helper.preventRouteChange(isDirty.current, (x) => isDirty.current = x, setConfirmOptions, saveAndNavigate, true);


    const [selectedQueryType, setSelectedQueryType] = useState<any>(props.query?.QueryType ?? null);
    const [selectedQueryStatus, setSelectedQueryStatus] = useState<any>(props.query?.QueryStatus ?? null);

    const [selectedCustomer, setSelectedCustomer] = useState<any>(props.query?.Customer ?? null);
    const [canChangeCustomer, setCanChangeCustomer] = useState(true);
    const [selectedCustomerName, setCustomerName] = useState('');

    const [selectedContact, setSelectedContact] = useState<any>(props.query?.Contact ?? null);
    const [selectedLocation, setSelectedLocation] = useState<any>(props.query?.Location ?? null);

    const [selectedEmployee, setSelectedEmployee] = useState<any>(props.query?.Employee ?? null);

    const [selectedQueryPriority, setSelectedQueryPriority] = useState<any>(!!props.query ? Enums.getEnumStringValue(Enums.QueryPriority, props.query.Priority) : null);

    const [selectedInventory, setSelectedInventory] = useState<any>(props.query?.Inventory ?? null);

    const selectCustomer = async (id) => {
        if (id) {
            const customer = await Fetch.get({
                url: `/Customer/${id}`,
            } as any);
            setSelectedCustomer(customer);
            setCustomerName(customer.CustomerName);
            setSelectedContact(customer.Contacts ? customer.Contacts[0] : undefined);
            setSelectedLocation(customer.Locations ? customer.Locations[0] : undefined);
        }
    };

    useEffect(() => {
        if(selectedQueryType?.ID) {
            form.setFieldValue('QueryTypeID', selectedQueryType.ID)
        } else {
            form.setFieldValue('QueryTypeID', null)
        }
    }, [selectedQueryType]);
    useEffect(() => {
        if(selectedQueryStatus?.ID) {
            form.setFieldValue('QueryStatusID', selectedQueryStatus.ID)
        } else {
            form.setFieldValue('QueryStatusID', null)
        }
    }, [selectedQueryStatus]);

    useEffect(() => {
        if(selectedQueryPriority) {
            form.setFieldValue('Priority', Enums.QueryPriority[selectedQueryPriority])
        } else {
            form.setFieldValue('Priority', null)
        }
    }, [selectedQueryPriority]);

    useEffect(() => {
        if(selectedInventory) {
            form.setFieldValue('InventoryID', selectedInventory?.ID)
        } else {
            form.setFieldValue('InventoryID', null)
        }
    }, [selectedInventory]);

    useEffect(() => {
        if(selectedCustomer?.ID) {
            form.setFieldValue('CustomerID', selectedCustomer.ID)
        } else {
            form.setFieldValue('CustomerID', null)
        }
    }, [selectedCustomer]);
    useEffect(() => {
        if(selectedContact?.ID) {
            form.setFieldValue('CustomerContactID', selectedContact.ID)
        } else {
            form.setFieldValue('CustomerContactID', null)
        }
    }, [selectedContact]);
    useEffect(() => {
        if(selectedLocation?.ID) {
            form.setFieldValue('LocationID', selectedLocation.ID)
        } else {
            form.setFieldValue('LocationID', null)
        }
    }, [selectedLocation]);

    useEffect(() => {
        if(selectedEmployee?.ID) {
            form.setFieldValue('EmployeeID', selectedEmployee.ID)
        } else {
            form.setFieldValue('EmployeeID', null)
        }
    }, [selectedEmployee]);

    useEffect(() => {
        if (props.module) {
            if (props.module === Enums.Module.Customer) {
                selectCustomer(props.module);
            }
            else if (props.module === Enums.Module.Quote) {
                setCanChangeCustomer(false);
            }
            else if (props.module === Enums.Module.JobCard) {
                setCanChangeCustomer(false);
                if (!!props.job.Store) {
                    setSelectedStore(props.job.Store);
                }
            }
        }
        getAccessStatus();
    }, []);

    useEffect(() => {
        if(props.customerID) {
            selectCustomer(props.customerID)
        }
    }, [props.customerID]);


    // STORES
    const [isMultiStore, setIsMultiStore] = useState(false);
    const [selectedStore, setSelectedStore] = useState<any>(props.query?.Store ?? null);

    const { data: storesData } = useQuery(['stores'], () => Fetch.get({
        url: `/Store/GetEmployeeStores?employeeID=${Storage.getCookie(Enums.Cookie.employeeID)}&searchPhrase=`,
    } as any))

    useEffect(() => {
        if (storesData) {
            setIsMultiStore(storesData.TotalResults > 1);
            if(storesData.TotalResults === 1 && !selectedStore) {
                setSelectedStore(storesData.Results[0])
                form.setFieldValue('StoreID', storesData.Results[0]?.ID)
            }
        }
    }, [storesData, selectedStore, form])


    const clearEmployee = (store) => {
        if (selectedEmployee && !Helper.isEmptyObject(selectedEmployee)) {
            setSelectedEmployee(null);
            form.setFieldValue('EmployeeID', null)
            showNotification({
                message: 'Employee has been cleared',
                autoClose: 2000,
            });
        }

        if(store?.ID) {
            form.setFieldValue('StoreID', store.ID)
        }
    }

    const queryMutation = useMutation(['query'], (payload) => queryPost(payload, !!props.query), {
        onSuccess: (data) => {
            if(navUrl.current) {
                Helper.nextRouter(Router.push, navUrl.current);
            } else if(!props.query) { // isNew
                Helper.nextRouter(Router.push, '/query/[id]', `/query/${data.Query.ID}`);
            }
            props.onSubmitState && props.onSubmitState('success')
            props.onSaved && props.onSaved(data)
            showNotification({
                id: 'createQuery',
                message: 'Query successfully ' + (!!props.query ? 'updated' : 'created'),
                loading: false,
                autoClose: 2000
            })
        },
        onError: (error: Error) => {
            const message = `Query could not be  ${!props.query ? 'created' : 'updated'}`
            showNotification({
                id: 'createQuery',
                message: error?.message || message,
                color: 'yellow',
                loading: false,
                autoClose: 3000
            });
            isDirty.current = true
            props.onSubmitState && props.onSubmitState('error')
        }
    })

    const handleSubmit = (values: any, e?: any, copy?: boolean) => {
        isDirty.current = false

        if(form.isValid()) {
            debouncedMutate({
                Query: {
                    ...(props.query),
                    ...values,
                    Date: Time.today(),
                    ClientDate: Time.today(),
                },
                ItemID: props.moduleID || null,
                Module: typeof props.module !== 'undefined' ? +props.module : null
            })
        } else {
            navUrl.current = ''
            // onNewStatus && onNewStatus('none')
            /*if (!isDirty && !form.validate().hasErrors && !hideSaveAndCancel) {
                // onInventorySaved(inventory);
            }*/

            scrollToErrors();
        }
    }

    useEffect(() => {
        if(props.setSubmitState === 'onSubmit') {
            handleSubmit(form.values)
        }
    }, [props.setSubmitState])

    return (
        <>

            <Title
                size={'lg'}
                fw={600}
            >
                {props.query && 'Edit Query Details' || 'Create Query'}
            </Title>

            {isMultiStore ?
                <div className="">
                    {/* <h3>Store</h3> */}
                    <StoreSelector
                        accessStatus={accessStatus}
                        error={form.getInputProps('Store').error}
                        required={true}
                        setSelectedStore={(e) => {
                            setSelectedStore(e);
                            form.setFieldValue('StoreID', e.ID)
                            clearEmployee(e)
                        }}
                        selectedStore={selectedStore}
                        cypress={undefined}
                    />

                </div> : ''
            }

            <form
                onSubmit={form.onSubmit(handleSubmit)}
            >
                <Box>
                    {/*<LoadingOverlay visible={assetFieldSettings.isLoading}/>*/}

                    <CustomerContactLocationSelector
                        isNew={!props.query}
                        selectedCustomer={selectedCustomer}
                        setSelectedCustomer={(customer) => {
                            setSelectedCustomer(customer)
                            props.onCustomerChange && props.onCustomerChange(customer)
                            form.setFieldValue('CustomerID', customer ? customer.ID : null)
                        }}
                        canChangeCustomer={canChangeCustomer}
                        selectedContact={selectedContact}
                        setSelectedContact={(contact) => {
                            setSelectedContact(contact)
                            contact !== null && form.setFieldValue('CustomerContactID', contact ? contact.ID : null)
                        }}
                        selectedLocation={selectedLocation}
                        setSelectedLocation={(location) => {
                            setSelectedLocation(location)
                            location !== null && form.setFieldValue('LocationID', location ? location.ID : null)
                        }}
                        locationRequired={isRequired('LocationID')}
                        inputErrors={{
                            Customer: form.errors.CustomerID,
                            Contact: form.errors.CustomerContactID,
                            Location: form.errors.LocationID,
                        }}
                        iconMode
                        module={Enums.Module.Query}
                        sendEmail={form.values.SendEmail}
                        setSendEmail={x => form.setFieldValue('SendEmail', x)}
                        sendSMS={form.values.SendSMS}
                        setSendSMS={x => form.setFieldValue('SendSMS', x)}
                        {...{} as any}
                    />

                    <SimpleGrid
                        spacing={{
                            sm: 'sm',
                            base: 'lg'
                        }}
                        verticalSpacing={0}
                        cols={{
                            sm: 2,
                            base: 1
                        }}
                    >
                        <QueryTypeSelector
                            accessStatus={accessStatus}
                            error={form.errors.QueryTypeID as string || ''}
                            placeholder="Select query type"
                            required={true}
                            selectedQueryType={selectedQueryType}
                            setSelectedQueryType={(e) => {
                                setSelectedQueryType(e);
                            }}
                            canClear={false}
                            ignoreAddOption={false}
                        />

                        <div className="status">

                            <QueryStatusSelector
                                accessStatus={accessStatus}
                                canClear={false}
                                error={form.errors.QueryStatusID as string || ''}
                                ignoreAddOption={false}
                                placeholder="Select query status"
                                queryType={selectedQueryType}
                                required={true}
                                selectedQueryStatus={selectedQueryStatus}
                                setSelectedQueryStatus={setSelectedQueryStatus}
                            />

                        </div>

                        <EmployeeSelector
                            accessStatus={accessStatus}
                            selectedEmployee={selectedEmployee}
                            setSelectedEmployee={setSelectedEmployee}
                            error={form.errors.EmployeeID}
                            storeID={form.values.StoreID}
                            required={true}
                        />

                        {
                            isShown('Reference') &&
                            <ScTextControl
                                label={'Reference'}
                                withAsterisk={isRequired('Reference')}
                                {...form.getInputProps(getFormNameForSystemName('Reference'))}
                            />
                        }


                        <SCDatePicker
                            canClear
                            label={'Follow up date'}
                            withAsterisk
                            {...form.getInputProps(getFormNameForSystemName('FollowupDate'))}
                        />

                        <SCDropdownList
                            label="Priority"
                            options={queryPriorities}
                            placeholder="Select required priority"
                            required={true}
                            onChange={(e) => {
                                setSelectedQueryPriority(e);
                            }}
                            value={selectedQueryPriority}
                            error={form.errors.Priority as string || ''}
                        />

                        {isShown('InventoryID') &&
                            <InventorySelector
                                onCreateNewInventoryItem={() => setShowCreateNewInventory(true)}
                                selectedInventory={selectedInventory}
                                setSelectedInventory={setSelectedInventory}
                                accessStatus={accessStatus}
                                error={form.getInputProps('InventoryID').error}
                                ignoreIDs={[]}
                                isRequired={isRequired('InventoryID')}
                                {...form.getInputProps('InventoryID')}
                            />
                        }


                    </SimpleGrid>

                    <SCTextArea
                        width={'100%'}
                        label="Description of the query"
                        {...form.getInputProps('Description')}
                        required={true}
                        customProps={{
                            w: '100% !important',
                            maw: '100%'
                        }}
                    />

                    <SimpleGrid
                        spacing={{
                            sm: 'var(--mantine-spacing-sm)',
                            base: 'var(--mantine-spacing-lg)'
                        }}
                        verticalSpacing={0}
                        cols={{
                            sm: 2,
                            base: 1
                        }}
                    >
                        {
 <>

                            {
                            }
                            {
                                isShown('CustomField1') &&
                                <ScTextControl
                                    label={settingsBySystemName[getSystemNameForFormName('CustomField1')].DisplayName}
                                    withAsterisk={isRequired('CustomField1')}
                                    {...form.getInputProps('CustomField1')}
                                />
                            }
                            {
                                isShown('CustomField2') &&
                                <ScTextControl
                                    label={settingsBySystemName[getSystemNameForFormName('CustomField2')].DisplayName}
                                    withAsterisk={isRequired('CustomField2')}
                                    {...form.getInputProps('CustomField2')}
                                />
                            }


                            {
                                isShown('CustomField3') &&
                                <ScTextControl
                                    label={settingsBySystemName[getSystemNameForFormName('CustomField3')].DisplayName}
                                    withAsterisk={isRequired('CustomField3')}
                                    {...form.getInputProps('CustomField3')}
                                />
                            }

                            {
                                isShown('CustomField4') &&
                                <ScTextControl
                                    label={settingsBySystemName[getSystemNameForFormName('CustomField4')].DisplayName}
                                    withAsterisk={isRequired('CustomField4')}
                                    {...form.getInputProps('CustomField4')}
                                />
                            }


                            {
                                isShown('CustomDate1') &&
                                <SCDatePicker
                                    canClear
                                    label={settingsBySystemName[getSystemNameForFormName('CustomDate1')].DisplayName}
                                    withAsterisk={isRequired('CustomDate1')}
                                    {...form.getInputProps('CustomDate1')}
                                />
                            }

                            {
                                isShown('CustomDate2') &&
                                <SCDatePicker
                                    canClear
                                    label={settingsBySystemName[getSystemNameForFormName('CustomDate2')].DisplayName}
                                    withAsterisk={isRequired('CustomDate2')}
                                    {...form.getInputProps('CustomDate2')}
                                />
                            }


                            {
                                isShown('CustomFilter1') &&
                                <SCSwitch
                                    label={settingsBySystemName[getSystemNameForFormName('CustomFilter1')].DisplayName}
                                    checked={form.values['CustomFilter1']}
                                    onToggle={(checked) => form.setFieldValue('CustomFilter1', checked)}
                                />
                            }
                            {
                                isShown('CustomFilter2') &&
                                <SCSwitch
                                    label={settingsBySystemName[getSystemNameForFormName('CustomFilter2')].DisplayName}
                                    checked={form.values['CustomFilter2']}
                                    onToggle={(checked) => form.setFieldValue('CustomFilter2', checked)}
                                />
                            }

                            {
                                isShown('CustomNumber1') &&
                                <ScNumberControl
                                    label={settingsBySystemName[getSystemNameForFormName('CustomNumber1')].DisplayName}
                                    withAsterisk={isRequired('CustomNumber1')}
                                    {...form.getInputProps('CustomNumber1')}
                                />
                            }

                            {
                                isShown('CustomNumber2') &&
                                <ScNumberControl
                                    label={settingsBySystemName[getSystemNameForFormName('CustomNumber2')].DisplayName}
                                    withAsterisk={isRequired('CustomNumber2')}
                                    {...form.getInputProps('CustomNumber2')}
                                />
                            }


                        </>
                        }
                    </SimpleGrid>


                </Box>

                {
                    _userAdmin &&
                    <>
                        <Space h={25}></Space>
                        <Link href={'/settings/query/manage'} style={{textDecoration: 'none'}}>
                            <Flex align={'center'} justify={'end'} c={'scBlue'} gap={5}>
                                <IconQuestionCircle size={16}/>
                                <Anchor size={'sm'}>Not seeing what you need? &nbsp;Add additional fields here.</Anchor>
                            </Flex>
                        </Link>
                    </>
                }


                {
                    !props.hideSaveAndCancel &&
                    <Group mt={'4rem'} justify={'right'} gap={'xs'}>
                        <Button type={'button'} variant={'subtle'} color={'gray.9'} onClick={() => {
                            props.onClose && props.onClose()
                        }}>
                            Cancel
                        </Button>
                        <Button
                            type={'submit'}
                            onClick={scrollToErrors}
                            disabled={queryMutation.isLoading || queryMutation.isSuccess}
                        >
                            {!props.query && 'Create' || 'Save'}
                        </Button>
                    </Group>
                }
            </form>


            <InventoryItemModal
                show={showCreateNewInventory}
                onInventorySave={(x) => {
                    setShowCreateNewInventory(false)
                    setSelectedInventory(x)
                }}
                onClose={() => setShowCreateNewInventory(false)}
                accessStatus={accessStatus}
                isNew
                backLabel={props.query && 'Edit Query Details' || 'Create Query'}
            />

            <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

        </>
    )
}

export default QueryForm